import { useState, type FormEvent } from "react";
import {
  useMe,
  useInviteUser,
  useRevokeUserAccess,
  useUpdateUserRole,
  useUpdateUserProfile,
  useResetUserPassword,
  useResetUserSecondFactor,
  useGenerateDriverPin,
  useUsers,
  useCounterparties,
  type InvitedUser,
} from "../api/hooks";
import { DataTable, createColumnHelper } from "../components/DataTable";
import { Button, Chip, ErrorNotice, Field, Input, Modal, Notice, PageHeader, Select } from "../components/ui";
import { IconPlus } from "../components/icons";
import { tenantRoleLabel } from "../lib/format";
import type { MeResponse, UserSummary } from "../api/hooks";

/**
 * Gestión de usuarios del tenant (CLAUDE.md Fase 4).
 *
 * Solo ADMIN llega a esta pantalla (el menú y la ruta ya lo filtran, ver
 * `permissions.ts`), pero las mismas reglas se repiten en el servidor: no se
 * puede cambiar el propio rol, ni dejar el tenant sin ningún ADMIN activo.
 *
 * El selector "Empresa" es opcional y solo tiene sentido cuando el tenant
 * opera varias empresas/cargadores (`Counterparty`) distintas: adscribe al
 * usuario a una de ellas para saber a qué empresa concreta pertenece dentro
 * del tenant-corporación.
 */

const ROLES = ["ADMIN", "GESTOR", "LECTOR", "CONDUCTOR"] as const;

/**
 * Pista de interfaz: super-admin puede con cualquiera; un ADMIN de empresa no
 * con otros ADMIN ni con super-admins. El servidor lo vuelve a comprobar (y
 * además mira si esa persona es ADMIN en otra empresa), así que esto solo
 * evita ofrecer un botón que acabaría en error.
 */
function canManageSecondFactor(me: MeResponse | undefined, target: UserSummary): boolean {
  if (!me) return false;
  if (me.isSuperAdmin) return true;
  return !target.isSuperAdmin && target.role !== "ADMIN";
}

const EMPTY_INVITE = { email: "", fullName: "", role: "LECTOR" as (typeof ROLES)[number], counterpartyId: "" };

export function UsersPage() {
  const me = useMe();
  // Un super-admin recibe `tenantId: undefined` del backend si no se pide
  // uno explícito (piensa que quiere el agregado de todos los tenants, algo
  // que este endpoint no soporta). Se pasa siempre el tenant activo de la
  // sesión (`me.data.tenantId`) para listar los usuarios de esta empresa.
  const query = useUsers(me.data?.tenantId);
  const counterparties = useCounterparties();
  const invite = useInviteUser();
  const updateRole = useUpdateUserRole();
  const updateProfile = useUpdateUserProfile();
  const resetPassword = useResetUserPassword();
  const revoke = useRevokeUserAccess();
  const resetSecondFactor = useResetUserSecondFactor();
  const generatePin = useGenerateDriverPin();

  const [open, setOpen] = useState(false);
  const [inviteValues, setInviteValues] = useState({ ...EMPTY_INVITE });
  const [created, setCreated] = useState<InvitedUser | null>(null);
  const [copied, setCopied] = useState(false);

  const [editing, setEditing] = useState<UserSummary | null>(null);
  const [editValues, setEditValues] = useState({ fullName: "", email: "", isActive: true, counterpartyId: "" });
  const [resetResult, setResetResult] = useState<string | null>(null);
  const [resetCopied, setResetCopied] = useState(false);
  const [secondFactorDone, setSecondFactorDone] = useState<string | null>(null);
  const [tempPin, setTempPin] = useState<{ pin: string; expiresAt: string } | null>(null);
  const [pinCopied, setPinCopied] = useState(false);

  const onInviteSubmit = (event: FormEvent): void => {
    event.preventDefault();
    invite.mutate(
      {
        email: inviteValues.email.trim(),
        fullName: inviteValues.fullName.trim(),
        role: inviteValues.role,
        ...(inviteValues.counterpartyId ? { counterpartyId: inviteValues.counterpartyId } : {}),
      },
      {
        onSuccess: (result) => {
          setCreated(result);
          setInviteValues({ ...EMPTY_INVITE });
          setOpen(false);
        },
      },
    );
  };

  const openEdit = (user: UserSummary): void => {
    setResetResult(null);
    setSecondFactorDone(null);
    setTempPin(null);
    resetSecondFactor.reset();
    generatePin.reset();
    setEditValues({
      fullName: user.fullName,
      email: user.email,
      isActive: user.isActive,
      counterpartyId: user.counterpartyId ?? "",
    });
    setEditing(user);
  };

  const onEditSubmit = (event: FormEvent): void => {
    event.preventDefault();
    if (!editing) return;
    updateProfile.mutate(
      {
        id: editing.id,
        values: {
          fullName: editValues.fullName.trim(),
          email: editValues.email.trim(),
          isActive: editValues.isActive,
          counterpartyId: editValues.counterpartyId || null,
        },
      },
      { onSuccess: () => setEditing(null) },
    );
  };

  const onResetPassword = (): void => {
    if (!editing) return;
    if (!window.confirm(`¿Generar una nueva contraseña provisional para ${editing.fullName}?`)) return;
    resetPassword.mutate(editing.id, {
      onSuccess: (result) => setResetResult(result.temporaryPassword),
    });
  };

  const onResetSecondFactor = (): void => {
    if (!editing) return;
    const what = editing.mfaEnabled ? "la verificación en dos pasos" : "el PIN de acceso";
    if (
      !window.confirm(
        `¿Quitar ${what} de ${editing.fullName}? Podrá entrar solo con su contraseña y volver a activarlo cuando quiera. Quedará registrado en la auditoría.`,
      )
    ) {
      return;
    }
    setTempPin(null);
    resetSecondFactor.mutate(editing.id, {
      onSuccess: () => {
        setSecondFactorDone(`Se ha quitado ${what}.`);
        setEditing((current) => (current ? { ...current, mfaEnabled: false, pinEnabled: false, pinMustChange: false } : current));
      },
    });
  };

  const onGeneratePin = (): void => {
    if (!editing) return;
    if (
      !window.confirm(
        `¿Generar un PIN temporal para ${editing.fullName}? Sustituye al PIN actual, caduca en 72 horas y tendrá que elegir uno propio al entrar.`,
      )
    ) {
      return;
    }
    setSecondFactorDone(null);
    generatePin.mutate(editing.id, {
      onSuccess: (result) => {
        setTempPin({ pin: result.temporaryPin, expiresAt: result.expiresAt });
        setEditing((current) => (current ? { ...current, pinEnabled: true, pinMustChange: true } : current));
      },
    });
  };

  const column = createColumnHelper<UserSummary>();
  const columns = [
    column.accessor("fullName", { header: "Nombre" }),
    column.accessor("email", {
      header: "Correo",
      cell: (info) => <span className="ident text-xs">{info.getValue()}</span>,
    }),
    column.display({
      id: "empresa",
      header: "Empresa",
      cell: (info) => info.row.original.counterpartyName ?? <span className="text-ink-400">—</span>,
    }),
    column.display({
      id: "role",
      header: "Rol",
      cell: (info) =>
        info.row.original.id === me.data?.id ? (
          <Chip tone="neutral">{tenantRoleLabel(info.row.original.role)} (tú)</Chip>
        ) : (
          <Select
            value={info.row.original.role}
            onChange={(event) =>
              updateRole.mutate({ id: info.row.original.id, role: event.target.value })
            }
            disabled={updateRole.isPending}
            className="w-auto"
          >
            {ROLES.map((option) => (
              <option key={option} value={option}>
                {tenantRoleLabel(option)}
              </option>
            ))}
          </Select>
        ),
    }),
    column.display({
      id: "secondFactor",
      header: "2.º factor",
      cell: (info) =>
        info.row.original.mfaEnabled ? (
          <Chip tone="ok">App</Chip>
        ) : info.row.original.pinEnabled ? (
          <Chip tone={info.row.original.pinMustChange ? "warn" : "ok"}>
            {info.row.original.pinMustChange ? "PIN temporal" : "PIN"}
          </Chip>
        ) : (
          <span className="text-ink-400">—</span>
        ),
    }),
    column.accessor("isActive", {
      header: "Estado",
      cell: (info) => (info.getValue() ? <Chip tone="ok">Activo</Chip> : <Chip tone="neutral">Inactivo</Chip>),
    }),
    column.display({
      id: "edit",
      header: "",
      cell: (info) => <Button onClick={() => openEdit(info.row.original)}>Editar</Button>,
    }),
    column.display({
      id: "revoke",
      header: "",
      cell: (info) =>
        info.row.original.id === me.data?.id ? null : (
          <Button
            variant="danger"
            onClick={() => {
              if (window.confirm(`¿Retirar el acceso de ${info.row.original.fullName} a esta empresa?`)) {
                revoke.mutate(info.row.original.id);
              }
            }}
          >
            Retirar acceso
          </Button>
        ),
    }),
  ];

  return (
    <>
      <PageHeader
        eyebrow="Configuración"
        title="Usuarios"
        description="Personas con acceso al panel de esta empresa, su rol y la empresa/cargador concreto al que pertenecen. Invitar a alguien nuevo crea su cuenta si no existía, o añade esta empresa a una cuenta ya existente."
        actions={
          <Button
            variant="primary"
            onClick={() => {
              setCreated(null);
              setInviteValues({ ...EMPTY_INVITE });
              setOpen(true);
            }}
          >
            <IconPlus className="h-4 w-4" />
            Invitar usuario
          </Button>
        }
      />

      {created ? (
        <div className="mb-4">
          <Notice tone={created.temporaryPassword ? "warn" : "ok"}>
            {created.temporaryPassword ? (
              <>
                <p className="font-semibold">
                  Copia la contraseña provisional ahora: no se volverá a mostrar.
                </p>
                <p className="mt-1">
                  {created.fullName} ({created.email}) debería cambiarla en su primer acceso.
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <code className="ident break-all rounded bg-ink-900 px-2 py-1 text-ink-50">
                    {created.temporaryPassword}
                  </code>
                  <Button
                    onClick={() => {
                      void navigator.clipboard.writeText(created.temporaryPassword ?? "").then(() => {
                        setCopied(true);
                        window.setTimeout(() => setCopied(false), 2000);
                      });
                    }}
                  >
                    {copied ? "Copiada" : "Copiar"}
                  </Button>
                </div>
              </>
            ) : (
              <p>
                {created.email} ya tenía cuenta: se le ha añadido acceso a esta empresa como{" "}
                {tenantRoleLabel(created.role)}.
              </p>
            )}
          </Notice>
        </div>
      ) : null}

      {invite.isError ? (
        <div className="mb-4">
          <ErrorNotice error={invite.error} />
        </div>
      ) : null}
      {updateRole.isError ? (
        <div className="mb-4">
          <ErrorNotice error={updateRole.error} />
        </div>
      ) : null}
      {revoke.isError ? (
        <div className="mb-4">
          <ErrorNotice error={revoke.error} />
        </div>
      ) : null}

      {query.isError ? (
        <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          data={query.data?.items ?? []}
          isLoading={query.isLoading}
          emptyMessage="No hay usuarios en esta empresa todavía."
        />
      )}

      <Modal open={open} title="Invitar usuario" onClose={() => setOpen(false)}>
        <form onSubmit={onInviteSubmit} className="space-y-3">
          <Field label="Nombre completo" htmlFor="u-name">
            <Input
              id="u-name"
              required
              value={inviteValues.fullName}
              onChange={(event) => setInviteValues({ ...inviteValues, fullName: event.target.value })}
              placeholder="Ana Pérez"
            />
          </Field>
          <Field label="Correo" htmlFor="u-email" hint="Si ya tiene cuenta en otra empresa, solo se le añade acceso a esta.">
            <Input
              id="u-email"
              type="email"
              required
              value={inviteValues.email}
              onChange={(event) => setInviteValues({ ...inviteValues, email: event.target.value })}
              placeholder="ana.perez@empresa.es"
            />
          </Field>
          <Field label="Rol" htmlFor="u-role">
            <Select
              id="u-role"
              value={inviteValues.role}
              onChange={(event) =>
                setInviteValues({ ...inviteValues, role: event.target.value as (typeof ROLES)[number] })
              }
            >
              {ROLES.map((option) => (
                <option key={option} value={option}>
                  {tenantRoleLabel(option)}
                </option>
              ))}
            </Select>
          </Field>
          <Field
            label="Empresa (opcional)"
            htmlFor="u-empresa"
            hint="Si el tenant opera varias empresas/cargadores, adscribe al usuario a una de ellas."
          >
            <Select
              id="u-empresa"
              value={inviteValues.counterpartyId}
              onChange={(event) => setInviteValues({ ...inviteValues, counterpartyId: event.target.value })}
            >
              <option value="">Sin asignar</option>
              {(counterparties.data?.items ?? []).filter((item) => item.isActive).map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </Select>
          </Field>

          <div className="flex gap-2">
            <Button type="submit" variant="primary" disabled={invite.isPending}>
              {invite.isPending ? "Invitando…" : "Invitar"}
            </Button>
            <Button onClick={() => setOpen(false)}>Cancelar</Button>
          </div>
        </form>
      </Modal>

      <Modal open={editing !== null} title="Editar usuario" onClose={() => setEditing(null)}>
        <form onSubmit={onEditSubmit} className="space-y-3">
          <Field label="Nombre completo" htmlFor="ue-name">
            <Input
              id="ue-name"
              required
              value={editValues.fullName}
              onChange={(event) => setEditValues({ ...editValues, fullName: event.target.value })}
            />
          </Field>
          <Field label="Correo" htmlFor="ue-email">
            <Input
              id="ue-email"
              type="email"
              required
              value={editValues.email}
              onChange={(event) => setEditValues({ ...editValues, email: event.target.value })}
            />
          </Field>
          <Field
            label="Empresa (opcional)"
            htmlFor="ue-empresa"
            hint="Empresa/cargador concreto al que pertenece dentro de esta empresa-tenant."
          >
            <Select
              id="ue-empresa"
              value={editValues.counterpartyId}
              onChange={(event) => setEditValues({ ...editValues, counterpartyId: event.target.value })}
            >
              <option value="">Sin asignar</option>
              {(counterparties.data?.items ?? [])
                .filter((item) => item.isActive || item.id === editValues.counterpartyId)
                .map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </Select>
          </Field>

          {editing && editing.id !== me.data?.id ? (
            <label className="flex items-center gap-2 text-sm text-ink-700 dark:text-ink-200">
              <input
                type="checkbox"
                checked={editValues.isActive}
                onChange={(event) => setEditValues({ ...editValues, isActive: event.target.checked })}
              />
              Cuenta activa
            </label>
          ) : (
            <p className="text-xs text-ink-500 dark:text-ink-400">
              No puedes desactivar tu propia cuenta desde aquí.
            </p>
          )}

          <div className="border-t border-ink-200 pt-3 dark:border-ink-800">
            <p className="micro-label mb-2">Contraseña</p>
            {resetResult ? (
              <Notice tone="warn">
                <p className="font-semibold">Copia la nueva contraseña provisional ahora: no se volverá a mostrar.</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <code className="ident break-all rounded bg-ink-900 px-2 py-1 text-ink-50">{resetResult}</code>
                  <Button
                    onClick={() => {
                      void navigator.clipboard.writeText(resetResult).then(() => {
                        setResetCopied(true);
                        window.setTimeout(() => setResetCopied(false), 2000);
                      });
                    }}
                  >
                    {resetCopied ? "Copiada" : "Copiar"}
                  </Button>
                </div>
              </Notice>
            ) : (
              <Button onClick={onResetPassword} disabled={resetPassword.isPending}>
                {resetPassword.isPending ? "Generando…" : "Generar nueva contraseña provisional"}
              </Button>
            )}
            {resetPassword.isError ? (
              <div className="mt-2">
                <ErrorNotice error={resetPassword.error} />
              </div>
            ) : null}
          </div>

          {editing && editing.id !== me.data?.id && canManageSecondFactor(me.data, editing) ? (
            <div className="border-t border-ink-200 pt-3 dark:border-ink-800">
              <p className="micro-label mb-2">Segundo factor de acceso</p>
              <p className="mb-2 text-xs text-ink-500 dark:text-ink-400">
                {editing.mfaEnabled
                  ? "Usa la app de autenticación."
                  : editing.pinEnabled
                    ? editing.pinMustChange
                      ? "Tiene un PIN temporal pendiente de cambiar."
                      : "Tiene un PIN de acceso."
                    : "No tiene segundo factor activado."}{" "}
                Cada persona activa el suyo en Ajustes; aquí solo puedes quitarlo si lo ha perdido
                {editing.role === "CONDUCTOR" ? " o generarle un PIN temporal" : ""}. No puedes ver sus códigos de
                recuperación.
              </p>
              {tempPin ? (
                <Notice tone="warn">
                  <p className="font-semibold">Copia el PIN temporal ahora: no se volverá a mostrar.</p>
                  <p className="mt-1 text-xs">
                    Caduca el {new Date(tempPin.expiresAt).toLocaleString("es-ES")}. Al entrar tendrá que elegir uno
                    propio.
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <code className="ident rounded bg-ink-900 px-2 py-1 text-lg tracking-widest text-ink-50">
                      {tempPin.pin}
                    </code>
                    <Button
                      onClick={() => {
                        void navigator.clipboard.writeText(tempPin.pin).then(() => {
                          setPinCopied(true);
                          window.setTimeout(() => setPinCopied(false), 2000);
                        });
                      }}
                    >
                      {pinCopied ? "Copiado" : "Copiar"}
                    </Button>
                  </div>
                </Notice>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {editing.mfaEnabled || editing.pinEnabled ? (
                    <Button variant="danger" onClick={onResetSecondFactor} disabled={resetSecondFactor.isPending}>
                      {resetSecondFactor.isPending
                        ? "Quitando…"
                        : editing.mfaEnabled
                          ? "Quitar verificación en dos pasos"
                          : "Quitar PIN"}
                    </Button>
                  ) : null}
                  {editing.role === "CONDUCTOR" && !editing.mfaEnabled ? (
                    <Button onClick={onGeneratePin} disabled={generatePin.isPending}>
                      {generatePin.isPending ? "Generando…" : "Generar PIN temporal"}
                    </Button>
                  ) : null}
                </div>
              )}
              {secondFactorDone ? (
                <p className="mt-2 text-xs text-signal-ok dark:text-teal-300">{secondFactorDone}</p>
              ) : null}
              {resetSecondFactor.isError ? (
                <div className="mt-2">
                  <ErrorNotice error={resetSecondFactor.error} />
                </div>
              ) : null}
              {generatePin.isError ? (
                <div className="mt-2">
                  <ErrorNotice error={generatePin.error} />
                </div>
              ) : null}
            </div>
          ) : null}

          {updateProfile.isError ? <ErrorNotice error={updateProfile.error} /> : null}

          <div className="flex gap-2">
            <Button type="submit" variant="primary" disabled={updateProfile.isPending}>
              {updateProfile.isPending ? "Guardando…" : "Guardar"}
            </Button>
            <Button onClick={() => setEditing(null)}>Cerrar</Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
